import { getPixelCropGeometry } from './imageEditorGeometry'
import type { ImageEdit } from './imageEditorGeometry'
import { createImageEditTransform } from './imageEditorTransform'
import {
  applyImageAppearanceToRgba,
  isIdentityImageAppearance,
} from './imageEditorAppearance'

export type ImageOutputMimeType = 'image/jpeg' | 'image/png' | 'image/webp'

export type RenderImageEditOptions = Readonly<{
  mimeType: ImageOutputMimeType
  quality?: number
  maxDimension?: number
  maxPixels?: number
  resizeToFitLimits?: boolean
}>

export type RenderedImageEdit = Readonly<{
  blob: Blob
  width: number
  height: number
  mimeType: string
}>

export const DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_DIMENSION = 4096
export const DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_PIXELS = 16_777_216

const assertQuality = (quality: number | undefined): void => {
  if (
    quality !== undefined &&
    (!Number.isFinite(quality) || quality < 0 || quality > 1)
  ) {
    throw new RangeError('quality must be between zero and one')
  }
}

const assertPositiveLimit = (limit: number, name: string): void => {
  if (!Number.isFinite(limit) || limit <= 0) {
    throw new RangeError(`${name} must be greater than zero`)
  }
}

type OutputGeometry = Readonly<{
  width: number
  height: number
  scaleX: number
  scaleY: number
}>

const getOutputGeometry = (
  width: number,
  height: number,
  options: RenderImageEditOptions,
): OutputGeometry => {
  const maxDimension =
    options.maxDimension ?? DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_DIMENSION
  const maxPixels = options.maxPixels ?? DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_PIXELS

  assertPositiveLimit(maxDimension, 'maxDimension')
  assertPositiveLimit(maxPixels, 'maxPixels')

  const exceedsDimension = width > maxDimension || height > maxDimension
  const exceedsArea = width * height > maxPixels
  if (!options.resizeToFitLimits && exceedsDimension) {
    throw new RangeError(
      'The edited image exceeds the maximum output dimension',
    )
  }

  if (!options.resizeToFitLimits && exceedsArea) {
    throw new RangeError('The edited image exceeds the maximum output area')
  }

  const scale = Math.min(
    1,
    maxDimension / width,
    maxDimension / height,
    Math.sqrt(maxPixels / (width * height)),
  )
  const outputWidth = Math.max(1, Math.floor(width * scale))
  const outputHeight = Math.max(1, Math.floor(height * scale))

  return {
    width: outputWidth,
    height: outputHeight,
    scaleX: outputWidth / width,
    scaleY: outputHeight / height,
  }
}

const ensureImageIsDecoded = async (image: HTMLImageElement): Promise<void> => {
  if (
    !image.complete ||
    image.naturalWidth === 0 ||
    image.naturalHeight === 0
  ) {
    await image.decode()
  }

  if (image.naturalWidth === 0 || image.naturalHeight === 0) {
    throw new Error('The image could not be decoded')
  }
}

const encodeCanvas = (
  canvas: HTMLCanvasElement,
  options: RenderImageEditOptions,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('The browser could not encode the edited image'))
        }
      },
      options.mimeType,
      options.quality,
    )
  })

/**
 * Renders color-managed source pixels into an sRGB output. The encoded edit
 * preserves the visible sRGB result, not the source file's embedded ICC profile
 * or colors outside the sRGB gamut.
 */
export const renderImageEdit = async (
  image: HTMLImageElement,
  edit: ImageEdit,
  options: RenderImageEditOptions,
): Promise<RenderedImageEdit> => {
  assertQuality(options.quality)
  await ensureImageIsDecoded(image)

  const sourceDimensions = {
    width: image.naturalWidth,
    height: image.naturalHeight,
  }
  const transform = createImageEditTransform(sourceDimensions, edit)
  const cropGeometry = getPixelCropGeometry(
    edit.crop,
    transform.stageDimensions,
  )
  const outputGeometry = getOutputGeometry(
    cropGeometry.width,
    cropGeometry.height,
    options,
  )

  const canvas = document.createElement('canvas')
  canvas.width = outputGeometry.width
  canvas.height = outputGeometry.height

  const context = canvas.getContext('2d', { colorSpace: 'srgb' })

  if (!context) {
    throw new Error('Canvas rendering is not available in this browser')
  }

  if (options.mimeType === 'image/jpeg') {
    context.fillStyle = '#fff'
    context.fillRect(0, 0, outputGeometry.width, outputGeometry.height)
  }

  context.save()

  try {
    if (outputGeometry.scaleX !== 1 || outputGeometry.scaleY !== 1) {
      context.scale(outputGeometry.scaleX, outputGeometry.scaleY)
    }
    context.translate(-cropGeometry.left, -cropGeometry.top)
    const { a, b, c, d, e, f } = transform.sourceToStage
    context.transform(a, b, c, d, e, f)
    context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight)
  } finally {
    context.restore()
  }

  if (!isIdentityImageAppearance(edit)) {
    const imageData = context.getImageData(
      0,
      0,
      outputGeometry.width,
      outputGeometry.height,
    )
    applyImageAppearanceToRgba(imageData.data, edit)
    context.putImageData(imageData, 0, 0)
  }

  const blob = await encodeCanvas(canvas, options)

  return {
    blob,
    width: outputGeometry.width,
    height: outputGeometry.height,
    mimeType: blob.type,
  }
}
